/**
 * VisitorRegisterPage.jsx — Public visitor / observer registration
 * ----------------------------------------------------------------
 * Mounted at `/events/:slug/visitor-register`.  The event microsite
 * surfaces a CTA to this page when the organiser has enabled
 * `visitor_registration_enabled` in event-access settings.
 *
 * Two-state flow:
 *   1. Form view (default)
 *   2. Confirmation view with the issued AccessPass + QR (if instant-
 *      approval) OR an "approval-required" notice (if pending).
 */
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  getPublicAccessInfo,
  visitorRegister,
  visitorPhotoPre,
  startCheckout,
  verifyRazorpay,
  formatApiError,
} from "../lib/accessPassApi";
import AccessPassCard from "../components/AccessPassCard";
import PublicShell from "../components/PublicShell";
import { Button } from "../components/ui/button";
import {
  ScanLine, MailCheck, CalendarRange, MapPin, Loader2,
  Building2, ShieldCheck, AlertTriangle, CheckCircle2, Camera, X,
  IndianRupee, CreditCard, Receipt,
} from "lucide-react";

const PASS_TYPE_LABEL = {
  visitor:   "General Visitor",
  observer:  "Observer",
  parent:    "Parent / Guardian",
  guest:     "Guest",
  speaker:   "Speaker",
  sponsor:   "Sponsor",
  press:     "Press / Media",
  volunteer: "Volunteer",
};

export default function VisitorRegisterPage() {
  const { slug } = useParams();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    pass_type: "visitor",
    organisation: "",
    purpose: "",
    day_session: "",
    consent: false,
  });
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [paymentInFlight, setPaymentInFlight] = useState(false);
  const [paymentInfo, setPaymentInfo] = useState(null);  // {amount, currency, billing_entity}

  // Load Razorpay checkout.js script once (only when needed).
  const ensureRazorpayJs = () => new Promise((resolve, reject) => {
    if (window.Razorpay) return resolve();
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("Razorpay script blocked or offline"));
    document.body.appendChild(s);
  });

  const handlePhotoSelect = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 4 * 1024 * 1024) {
      toast.error("Photo must be under 4 MB.");
      return;
    }
    if (!["image/jpeg", "image/png", "image/webp"].includes(f.type)) {
      toast.error("Only JPEG, PNG or WebP photos are allowed.");
      return;
    }
    setPhotoFile(f);
    setPhotoPreview(URL.createObjectURL(f));
  };

  const clearPhoto = () => {
    setPhotoFile(null);
    if (photoPreview) URL.revokeObjectURL(photoPreview);
    setPhotoPreview(null);
  };

  useEffect(() => {
    (async () => {
      try {
        const data = await getPublicAccessInfo(slug);
        setInfo(data);
        if (data?.self_select_types?.length) {
          setForm((f) => ({ ...f, pass_type: data.self_select_types[0] }));
        }
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.consent) {
      toast.error("Please accept the event terms to continue.");
      return;
    }
    setSubmitting(true);
    try {
      // 1. Upload photo first (if any) → get photo_token
      let photo_token = null;
      if (photoFile) {
        setPhotoUploading(true);
        const upload = await visitorPhotoPre(photoFile);
        photo_token = upload.photo_token;
        setPhotoUploading(false);
      }
      const res = await visitorRegister(info.event_id, { ...form, photo_token });
      setResult(res);

      // 2. Paid pass? Kick off Razorpay / Stripe checkout immediately.
      if (res.requires_payment) {
        await triggerCheckout(res.pass.id);
      } else {
        toast.success(
          res.approval_required
            ? "Registration received — pending organiser approval."
            : "AccessPass issued! Save the QR below."
        );
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
      setPhotoUploading(false);
    }
  };

  // ── Razorpay / Stripe checkout orchestrator
  const triggerCheckout = async (passId) => {
    setPaymentInFlight(true);
    try {
      const co = await startCheckout(passId, {});
      setPaymentInfo({
        amount: co.amount,
        currency: co.currency,
        billing_entity: co.billing_entity,
        billing_entity_name: co.billing_entity_name,
        tax_breakup: co.tax_breakup,
        gateway: co.gateway,
      });

      // Mock mode → simulate verify without opening Checkout.js (safe
      // for preview / live-keys env).
      if (co.checkout?.mode === "mock") {
        try {
          const verify = await verifyRazorpay({
            pass_id:             passId,
            razorpay_order_id:   co.checkout.order_id,
            razorpay_payment_id: `pay_mock_${Date.now()}`,
            razorpay_signature:  `mock_signature_${Date.now()}`,
          });
          setResult((prev) => ({
            ...prev,
            pass: { ...verify.pass, qr_token: verify.qr_token },
            requires_payment: false,
            paid: true,
          }));
          toast.success("Mock payment confirmed · AccessPass active.");
        } catch (e) {
          toast.error(formatApiError(e));
        } finally {
          setPaymentInFlight(false);
        }
        return;
      }

      if (co.gateway === "razorpay") {
        try {
          await ensureRazorpayJs();
        } catch (e) {
          toast.error("Could not load payment gateway — please check your network.");
          setPaymentInFlight(false);
          return;
        }
        const opts = {
          key:        co.checkout.key_id,
          amount:     co.checkout.amount,
          currency:   co.checkout.currency,
          name:       "Diplomexa AccessPass",
          description: `${info.event_title} · ${form.pass_type}`,
          order_id:   co.checkout.order_id,
          prefill:    co.checkout.prefill,
          notes:      co.checkout.notes,
          theme:      { color: "#0A192F" },
          handler: async (response) => {
            try {
              const verify = await verifyRazorpay({
                pass_id:             passId,
                razorpay_order_id:   response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature:  response.razorpay_signature,
              });
              setResult((prev) => ({
                ...prev,
                pass: { ...verify.pass, qr_token: verify.qr_token },
                requires_payment: false,
                paid: true,
              }));
              toast.success("Payment successful! Your AccessPass is now active.");
            } catch (e) {
              toast.error(formatApiError(e));
            } finally {
              setPaymentInFlight(false);
            }
          },
          modal: {
            ondismiss: () => {
              setPaymentInFlight(false);
              toast.warning("Payment cancelled. Your pass is still pending — you can complete it later from this page.");
            },
          },
        };
        const rzp = new window.Razorpay(opts);
        rzp.on("payment.failed", () => {
          toast.error("Payment failed — please try again.");
          setPaymentInFlight(false);
        });
        rzp.open();
      } else if (co.gateway === "stripe") {
        if (co.checkout?.checkout_url) {
          window.location.href = co.checkout.checkout_url;
        } else {
          toast.error("Stripe checkout URL missing.");
          setPaymentInFlight(false);
        }
      } else {
        toast.error("Unsupported payment gateway.");
        setPaymentInFlight(false);
      }
    } catch (err) {
      const det = err?.response?.data?.detail;
      if (typeof det === "object" && det?.code === "foreign_payments_not_configured") {
        toast.error(det.reason || "Foreign payments are not available for this event yet.");
      } else {
        toast.error(formatApiError(err));
      }
      setPaymentInFlight(false);
    }
  };

  if (loading) {
    return (
      <PublicShell>
        <div className="flex items-center justify-center py-32 text-slate-500">
          <Loader2 className="h-5 w-5 animate-spin mr-2" />
          Loading event…
        </div>
      </PublicShell>
    );
  }
  if (!info) {
    return (
      <PublicShell>
        <div className="max-w-md mx-auto py-24 px-6 text-center">
          <h1 className="text-2xl font-bold text-[#0A192F]">Event not found</h1>
        </div>
      </PublicShell>
    );
  }
  if (!info.visitor_registration_enabled) {
    return (
      <PublicShell>
        <div className="max-w-md mx-auto py-24 px-6 text-center" data-testid="visitor-register-disabled">
          <AlertTriangle className="h-10 w-10 mx-auto text-amber-500" />
          <h1 className="mt-4 text-2xl font-bold text-[#0A192F]">
            Visitor registration is not open
          </h1>
          <p className="mt-2 text-sm text-slate-500">
            The organisers of <b>{info.event_title}</b> haven&apos;t enabled
            public visitor passes yet. Please check back closer to the event.
          </p>
        </div>
      </PublicShell>
    );
  }

  if (result) {
    const paymentPending = result.requires_payment === true;
    return (
      <PublicShell>
        <div className="max-w-3xl mx-auto py-12 px-4 sm:px-6">
          <div className="text-center mb-8">
            {paymentPending ? (
              <>
                <div className="inline-flex h-14 w-14 rounded-full bg-amber-100 items-center justify-center">
                  <CreditCard className="h-7 w-7 text-amber-600" />
                </div>
                <h1
                  className="mt-4 text-3xl font-bold text-[#0A192F]"
                  data-testid="visitor-register-payment-pending"
                >
                  Payment pending
                </h1>
                <p className="mt-2 text-sm text-slate-600 max-w-lg mx-auto">
                  Your pass is reserved with ID{" "}
                  <code className="font-mono text-xs">{result.pass.id.slice(-10).toUpperCase()}</code>.
                  Complete payment below to activate the QR.
                </p>
                {paymentInfo && (
                  <div className="mt-3 inline-flex items-center gap-2 text-sm text-slate-700 rounded-full bg-slate-50 border px-3 py-1">
                    <IndianRupee className="h-3.5 w-3.5 text-[#D4AF37]" />
                    <span><b>{paymentInfo.currency} {paymentInfo.amount}</b> · billed by {paymentInfo.billing_entity_name}</span>
                  </div>
                )}
                <div className="mt-5">
                  <Button
                    onClick={() => triggerCheckout(result.pass.id)}
                    disabled={paymentInFlight}
                    className="bg-[#0A192F] hover:bg-[#0F2C4F]"
                    data-testid="visitor-retry-payment"
                  >
                    {paymentInFlight ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <CreditCard className="h-4 w-4 mr-2" />
                    )}
                    Pay {paymentInfo?.currency || "INR"} {paymentInfo?.amount || result.paid_pass_amount}
                  </Button>
                </div>
              </>
            ) : result.approval_required ? (
              <>
                <div className="inline-flex h-14 w-14 rounded-full bg-amber-100 items-center justify-center">
                  <MailCheck className="h-7 w-7 text-amber-600" />
                </div>
                <h1
                  className="mt-4 text-3xl font-bold text-[#0A192F]"
                  data-testid="visitor-register-pending"
                >
                  Awaiting organiser approval
                </h1>
                <p className="mt-2 text-sm text-slate-600 max-w-lg mx-auto">
                  Your AccessPass will activate once the organisers review and
                  approve your registration. You&apos;ll receive a confirmation
                  with your QR code once approved.
                </p>
              </>
            ) : (
              <>
                <div className="inline-flex h-14 w-14 rounded-full bg-emerald-100 items-center justify-center">
                  <CheckCircle2 className="h-7 w-7 text-emerald-600" />
                </div>
                <h1
                  className="mt-4 text-3xl font-bold text-[#0A192F]"
                  data-testid="visitor-register-success"
                >
                  AccessPass issued
                </h1>
                <p className="mt-2 text-sm text-slate-600 max-w-lg mx-auto">
                  Save or screenshot the QR below. Present it at the venue
                  entrance — our team will scan it to admit you.
                </p>
              </>
            )}
          </div>
          <div className="flex justify-center">
            <AccessPassCard
              pass={result.pass}
              qrToken={result.pass?.qr_token}
            />
          </div>
          {info.venue_instructions && (
            <div className="mt-8 rounded-xl bg-slate-50 border border-slate-200 p-4 text-sm text-slate-700">
              <p className="font-semibold mb-1">Venue instructions</p>
              <p>{info.venue_instructions}</p>
            </div>
          )}
        </div>
      </PublicShell>
    );
  }

  return (
    <PublicShell>
      <div className="max-w-4xl mx-auto py-10 px-4 sm:px-6">
        <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">
          Diplomexa AccessPass
        </p>
        <h1 className="mt-2 text-3xl sm:text-4xl font-bold text-[#0A192F]">
          Register to visit {info.event_title}
        </h1>
        <p className="mt-2 text-slate-600 max-w-2xl">
          {info.welcome_message ||
            "Reserve your visitor AccessPass. You'll get a secure QR you can scan at the entrance."}
        </p>

        <div className="mt-6 grid sm:grid-cols-3 gap-3 text-sm text-slate-600">
          <div className="rounded-lg border bg-white px-3 py-2 flex items-center gap-2">
            <CalendarRange className="h-4 w-4 text-[#0F4C81]" />
            <span>
              {info.start_date ? new Date(info.start_date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"}
              {" → "}
              {info.end_date ? new Date(info.end_date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"}
            </span>
          </div>
          <div className="rounded-lg border bg-white px-3 py-2 flex items-center gap-2">
            <MapPin className="h-4 w-4 text-[#0F4C81]" />
            <span>{info.location || "Venue TBC"}</span>
          </div>
          <div className="rounded-lg border bg-white px-3 py-2 flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-[#0F4C81]" />
            <span>QR validated server-side</span>
          </div>
        </div>

        <form
          onSubmit={handleSubmit}
          className="mt-8 rounded-2xl bg-white border border-slate-200 p-6 sm:p-8 shadow-sm space-y-5"
          data-testid="visitor-register-form"
        >
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Full name *" required>
              <input
                type="text"
                required
                value={form.full_name}
                onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                className={inputCls}
                data-testid="visitor-register-full-name"
              />
            </Field>
            <Field label="Email *" required>
              <input
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className={inputCls}
                data-testid="visitor-register-email"
              />
            </Field>
            <Field label="Mobile / WhatsApp *" required>
              <input
                type="tel"
                required
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className={inputCls}
                data-testid="visitor-register-phone"
              />
            </Field>
            <Field label="Pass category *" required>
              <select
                value={form.pass_type}
                onChange={(e) => setForm({ ...form, pass_type: e.target.value })}
                className={inputCls}
                data-testid="visitor-register-pass-type"
              >
                {(info.self_select_types || []).map((t) => {
                  const paidConf = info.paid_passes?.[t];
                  const isPaid = paidConf?.enabled && paidConf?.active !== false;
                  const isInstant = (info.instant_approval_pass_types || []).includes(t);
                  let suffix = "";
                  if (isPaid) {
                    suffix = ` · ${paidConf.currency_default || "INR"} ${paidConf.amount_inr || paidConf.amount_usd}`;
                  } else if (isInstant) {
                    suffix = " · Instant QR";
                  } else {
                    suffix = " · Needs approval";
                  }
                  return (
                    <option key={t} value={t}>
                      {PASS_TYPE_LABEL[t] || t}{suffix}
                    </option>
                  );
                })}
              </select>
            </Field>
            <Field label="Organisation / school / company">
              <input
                type="text"
                value={form.organisation}
                onChange={(e) =>
                  setForm({ ...form, organisation: e.target.value })
                }
                className={inputCls}
                data-testid="visitor-register-organisation"
              />
            </Field>
            <Field label="Day / session preference">
              <input
                type="text"
                placeholder="e.g. Day 1 — Opening Ceremony"
                value={form.day_session}
                onChange={(e) =>
                  setForm({ ...form, day_session: e.target.value })
                }
                className={inputCls}
                data-testid="visitor-register-day-session"
              />
            </Field>
          </div>
          <Field label="Purpose of visit">
            <textarea
              rows={3}
              value={form.purpose}
              onChange={(e) => setForm({ ...form, purpose: e.target.value })}
              className={inputCls}
              data-testid="visitor-register-purpose"
            />
          </Field>

          {/* Photo upload (optional) */}
          <div>
            <span className="text-xs font-semibold text-slate-600 mb-1 block">
              Photo (optional — helps the entrance team verify you faster)
            </span>
            {photoPreview ? (
              <div className="flex items-center gap-3">
                <img
                  src={photoPreview}
                  alt="Preview"
                  className="h-20 w-20 rounded-lg object-cover border-2 border-[#D4AF37]/60"
                  data-testid="visitor-photo-preview"
                />
                <button
                  type="button"
                  onClick={clearPhoto}
                  className="text-xs text-rose-600 underline"
                  data-testid="visitor-photo-clear"
                >
                  Remove photo
                </button>
              </div>
            ) : (
              <label
                className="inline-flex items-center gap-2 cursor-pointer rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 px-4 py-3 text-sm text-slate-600 hover:border-[#D4AF37] hover:bg-[#D4AF37]/5 transition-colors"
                data-testid="visitor-photo-dropzone"
              >
                <Camera className="h-4 w-4 text-[#D4AF37]" />
                <span>Upload photo (JPEG/PNG/WebP, max 4 MB)</span>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handlePhotoSelect}
                  className="hidden"
                  data-testid="visitor-photo-input"
                />
              </label>
            )}
          </div>

          <label className="flex items-start gap-3 text-sm text-slate-600 cursor-pointer">
            <input
              type="checkbox"
              checked={form.consent}
              onChange={(e) => setForm({ ...form, consent: e.target.checked })}
              className="mt-0.5"
              data-testid="visitor-register-consent"
            />
            <span>
              I agree to the event terms.{" "}
              <span className="text-slate-500">
                {info.terms_text ||
                  "By entering the venue you agree to abide by the event code of conduct."}
              </span>
            </span>
          </label>

          <Button
            type="submit"
            disabled={submitting}
            className="w-full bg-[#0A192F] hover:bg-[#0F2C4F] text-white"
            data-testid="visitor-register-submit"
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Submitting…
              </>
            ) : (
              <>
                <ScanLine className="h-4 w-4 mr-2" />
                Get my AccessPass
              </>
            )}
          </Button>

          <p className="text-[11px] text-slate-400 text-center">
            Already registered as a delegate?{" "}
            <Link to="/login" className="text-[#0F4C81] font-medium hover:underline">
              Sign in to view your delegate pass
            </Link>
          </p>
        </form>
      </div>
    </PublicShell>
  );
}

const inputCls =
  "w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-[#0A192F] focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/40 focus:border-[#D4AF37]/60";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold text-slate-600 mb-1 block">
        {label}
      </span>
      {children}
    </label>
  );
}
